import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import Logo from "@/components/Logo";
import { formatApiError } from "@/lib/api";
import { LineChart, Lock } from "lucide-react";

export default function Login() {
  const { login, user } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  if (user) {
    const dest = user.role === "admin" ? "/admin" : user.role === "data_entry" ? "/invoices" : "/dashboard";
    return <Navigate to={dest} replace />;
  }

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const u = await login(email, password);
      const dest = u.role === "admin" ? "/admin" : u.role === "data_entry" ? "/invoices" : "/dashboard";
      navigate(dest, { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex font-body bg-white">
      {/* Left visual panel */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-blue-900 via-blue-800 to-blue-950 text-white p-12 flex-col items-center justify-center">
        <Logo size={160} />
        <div className="mt-6 text-sm font-semibold uppercase tracking-[0.3em] text-blue-200">
          Sales Tracker
        </div>
      </div>

      {/* Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <form onSubmit={submit} className="w-full max-w-sm space-y-6" data-testid="login-form">
          <div>
            <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 mb-2">
              <Lock size={14} strokeWidth={1.5} /> Sign in
            </div>
            <h1 className="text-3xl font-extrabold tracking-tighter font-heading">Welcome back</h1>
            <p className="text-sm text-zinc-500 mt-1">Use your work email to access the sales console.</p>
          </div>

          {error && (
            <div className="border border-rose-200 bg-rose-50 text-rose-700 text-sm p-3 rounded-md" data-testid="login-error">
              {error}
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="email" className="font-semibold">Email</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
              required
              data-testid="login-email-input"
              autoComplete="email"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password" className="font-semibold">Password</Label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              required
              data-testid="login-password-input"
              autoComplete="current-password"
            />
          </div>

          <Button
            type="submit"
            disabled={submitting}
            className="w-full bg-blue-700 hover:bg-blue-800 text-white"
            data-testid="login-submit-button"
          >
            {submitting ? "Signing in…" : "Sign in"}
          </Button>
        </form>
      </div>
    </div>
  );
}
